import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PaymentElementLibrary = ({ onElementClick }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeCategory, setActiveCategory] = useState('payment');

  const elementCategories = [
    {
      id: 'payment',
      name: 'Payment Fields',
      icon: 'CreditCard',
      elements: [
        { id: 'card', name: 'Credit Card', icon: 'CreditCard', type: 'payment-card', description: 'Secure card input with Stripe' },
        { id: 'amount', name: 'Amount Field', icon: 'DollarSign', type: 'payment-amount', description: 'Variable amount input' },
        { id: 'currency', name: 'Currency Selector', icon: 'Coins', type: 'payment-currency', description: 'Multi-currency support' },
        { id: 'payment-method', name: 'Payment Method', icon: 'Wallet', type: 'payment-method', description: 'Card, wallet, bank transfer' }
      ]
    },
    {
      id: 'billing',
      name: 'Billing & Shipping',
      icon: 'MapPin',
      elements: [
        { id: 'billing-address', name: 'Billing Address', icon: 'Home', type: 'billing-address', description: 'Complete billing address form' },
        { id: 'shipping-address', name: 'Shipping Address', icon: 'Truck', type: 'shipping-address', description: 'Delivery address form' },
        { id: 'name', name: 'Full Name', icon: 'User', type: 'input', placeholder: 'John Doe', required: true },
        { id: 'email', name: 'Email', icon: 'Mail', type: 'input', placeholder: 'email@example.com', required: true },
        { id: 'phone', name: 'Phone', icon: 'Phone', type: 'input', placeholder: '+1 (555) 000-0000', required: false }
      ]
    },
    {
      id: 'subscription',
      name: 'Subscription',
      icon: 'RefreshCw',
      elements: [
        { id: 'plan-selector', name: 'Plan Selector', icon: 'Layers', type: 'subscription-plan', description: 'Choose subscription tier' },
        { id: 'interval', name: 'Billing Interval', icon: 'Calendar', type: 'subscription-interval', description: 'Monthly, yearly, etc.' },
        { id: 'trial', name: 'Trial Period', icon: 'Clock', type: 'subscription-trial', description: 'Free trial configuration' }
      ]
    },
    {
      id: 'checkout',
      name: 'Checkout Elements',
      icon: 'ShoppingCart',
      elements: [
        { id: 'order-summary', name: 'Order Summary', icon: 'FileText', type: 'order-summary', description: 'Cart items and totals' },
        { id: 'discount-code', name: 'Discount Code', icon: 'Tag', type: 'discount-code', description: 'Promo code input' },
        { id: 'tax-calculator', name: 'Tax Calculator', icon: 'Calculator', type: 'tax-calculator', description: 'Automatic tax calculation' },
        { id: 'terms', name: 'Terms & Conditions', icon: 'FileCheck', type: 'terms-checkbox', description: 'Legal agreement checkbox' }
      ]
    },
    {
      id: 'security',
      name: 'Security & Compliance',
      icon: 'Shield',
      elements: [
        { id: 'ssl-badge', name: 'SSL Badge', icon: 'Lock', type: 'security-badge', description: 'Trust indicator' },
        { id: 'pci-badge', name: 'Secure Payments', icon: 'ShieldCheck', type: 'pci-badge', description: 'Stripe-secured payments badge' },
        { id: 'privacy-notice', name: 'Privacy Notice', icon: 'Eye', type: 'privacy-notice', description: 'Data protection info' }
      ]
    }
  ];

  const handleElementDragStart = (e, element) => {
    e?.dataTransfer?.setData('element', JSON.stringify(element));
  };

  if (isCollapsed) {
    return (
      <div className="w-16 bg-card border-r border-border flex flex-col items-center py-4 gap-4">
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-2 hover:bg-muted rounded-md transition-smooth"
        >
          <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
        </button>
        {elementCategories?.map(category => (
          <button
            key={category?.id}
            onClick={() => {
              setActiveCategory(category?.id);
              setIsCollapsed(false);
            }}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
            title={category?.name}
          >
            <Icon name={category?.icon} size={20} className="text-muted-foreground" />
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-r border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">Payment Elements</h2>
        <button
          onClick={() => setIsCollapsed(true)}
          className="p-1 hover:bg-muted rounded-md transition-smooth"
        >
          <Icon name="ChevronLeft" size={18} className="text-muted-foreground" />
        </button>
      </div>

      {/* Category Tabs */}
      <div className="border-b border-border overflow-x-auto">
        <div className="flex">
          {elementCategories?.map(category => (
            <button
              key={category?.id}
              onClick={() => setActiveCategory(category?.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth border-b-2
                ${activeCategory === category?.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }
              `}
            >
              <Icon name={category?.icon} size={16} />
              <span className="whitespace-nowrap">{category?.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Elements List */}
      <div className="flex-1 overflow-y-auto p-4">
        {elementCategories?.find(cat => cat?.id === activeCategory)?.elements?.map(element => (
          <div
            key={element?.id}
            draggable
            onDragStart={(e) => handleElementDragStart(e, element)}
            onClick={() => onElementClick(element)}
            className="
              mb-3 p-4 bg-background border border-border rounded-lg cursor-move
              hover:border-primary hover:shadow-sm transition-smooth group
            "
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-md group-hover:bg-primary/20 transition-smooth">
                <Icon name={element?.icon} size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-foreground mb-1">
                  {element?.name}
                </h3>
                {element?.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {element?.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Help Section */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Drag & Drop</p>
            <p>Click or drag elements to the canvas to build your payment form.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentElementLibrary;